<template>
    <div v-if="isSearchParamsSet">
        <progress-header />

        <div class="container mt-4">
            <div class="row">
                <div class="col-12 col-lg-9 mb-2">
                    <div class="card shadow-sm px-4 py-3 mb-4" v-if="!isLoggedIn">
                        <div class="row align-items-center justify-content-between">
                            <div class="col-12 col-md-9 text-blue-600 font-weight-bold">
                                {{ $t('taxi.pages.passengers.login_in_to_speed_up_process') }}
                            </div>

                            <div class="col-12 col-md-3 text-right">
                                <router-link :to="{ name: 'login.index', query: { redirectTo: '/berbus/passengers' } }" class="btn btn-sm btn-outline-primary">
                                    {{ $t('auth.login_header') }}
                                </router-link>
                            </div>
                        </div>
                    </div>

                    <form @submit.prevent="handleSubmit">
                        <div class="card booking-form-card shadow-sm">
                            <div class="card-header">
                                {{ $t('taxi.pages.passengers.passengers_data') }}
                            </div>

                            <div class="px-4 py-3">
                                <div class="mb-4" v-for="(passenger, idx) in passengers" :key="idx">
                                    <passengers-passenger :passenger="passenger" :idx="idx"
                                        @update="onPassengerUpdated"
                                    />

                                    <div class="row" v-if="idx === 0 && isAnyAddressAirport">
                                        <div class="col-12 col-lg-4">
                                            <div class="mb-1">
                                                {{ $t('flight_number') }}:
                                            </div>

                                            <div>
                                                <input class="form-control" maxlength="50" v-model="flightNumber">
                                            </div>
                                        </div>

                                        <div class="col-12 col-lg-4" v-if="idx === 0">
                                            <div class="mb-1 is-required">
                                                {{ departureArrivalTimeLabel }}:
                                            </div>

                                            <div>
                                                <vue-timepicker
                                                    placeholder=" "
                                                    :minute-interval="5"
                                                    v-model="flightTime"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="my-4">
                            <button type="submit" class="btn btn-primary">
                                {{ $t('next') }}
                            </button>
                        </div>
                    </form>
                </div>

                <div class="col-12 col-lg-3">
                    <the-cart />
                </div>

                <div class="col-12 col-lg-9">
                    <stop-info />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import toastr from 'toastr';
    import VueTimepicker from 'vue2-timepicker';
    import 'vue2-datepicker/index.css';

    import PassengersPassenger from '@components/Taxi/PassengersPassenger';
    import TheCart from './components/Cart/TheCart';
    import ProgressHeader from './components/ProgressHeader';
    import {trackGA} from '@utilities/tracking';
    import i18n from '@/i18n';
    import StopInfo from './components/StopInfo';

    export default {
        components: {
            VueTimepicker,
            PassengersPassenger,
            ProgressHeader,
            StopInfo,
            TheCart,
        },

        computed: {
            ...mapGetters({
                isSearchParamsSet: 'berbus/isSearchSet',
                travel: 'berbus/getTravel',
                passengers: 'berbus/getPassengers',
                isLoggedIn: 'user/isLoggedIn',
                passengersTotal: 'berbus/passengersTotal',
            }),

            flightNumber: {
                get() {
                    return this.$store.getters['berbus/getFlightNumber'];
                },
                set(value) {
                    this.$store.commit('berbus/setFlightNumber', value);
                },
            },

            flightTime: {
                get() {
                    return this.$store.getters['berbus/getFlightTime'];
                },
                set(value) {
                    this.$store.commit('berbus/setFlightTime', value);
                },
            },

            isAnyAddressAirport() {
                return this.travel.start_is_airport || this.travel.end_is_airport;
            },

            departureArrivalTimeLabel() {
                return this.travel.start_is_airport?(i18n.tc('berbus.pages.passengers.flight_time_to') + ' ' + this.parseAirportName(this.travel.start_place)):(i18n.tc('berbus.pages.passengers.flight_time_from') + ' ' + this.parseAirportName(this.travel.end_place));
            },
        },

        created() {
            trackGA('checkout_progress', {
                checkout_step: 4,
                checkout_option: window.location.pathname,
            });

            if (!this.isSearchParamsSet) {
                this.$router.push({ name: 'berbus.index' });

                return;
            }

            if (!this.travel) {
                this.$router.push({ name: 'berbus.vehicles' });

                return;
            }

            if (this.passengers.length === 0) {
                this.$store.dispatch('berbus/setPassengers', this.createPassengers(this.passengersTotal || 1));
            }
        },

        methods: {
            handleSubmit() {
                if ((this.travel.start_is_airport || this.travel.end_is_airport) && !this.flightTime) {
                    toastr.error(this.$t('time_invalid', { field: this.$t('berbus.pages.passengers.flight_time') }));
                    return;
                }
                if (this.flightTime && !this.flightTime.match(/^[0-9]{2}:[0-9]{2}$/)) {
                    toastr.error(this.$t('time_invalid', { field: this.$t('berbus.pages.passengers.flight_time') }));
                    return;
                }

                this.$router.push({name: 'berbus.options'});
            },

            createPassengers(quantity = 1) {
                const passengers = [];

                for (let i = 1; i <= quantity; i++) {
                    passengers.push(
                        this.passengerModel()
                    );
                }

                return passengers;
            },

            onPassengerUpdated(idx, data) {
                const passengers = JSON.parse(JSON.stringify(this.passengers));

                passengers[idx] = data;

                this.$store.dispatch('berbus/setPassengers', passengers);
            },

            passengerModel() {
                return {
                    first_name: null,
                    last_name: null,
                    phone: null,
                };
            },

            parseAirportName(place) {
                const suffix = i18n.locale==='pl'?'a':'';
                return place.match(/BER$/)?'Berlin'+suffix:place;
            },
        },
    };
</script>

<style scoped>
    .vue__time-picker {
        width: 100%;
    }

    .vue__time-picker >>> input.display-time {
        background-color: #f7f7f7;
        border: 1px solid #ddd;
        border-radius: 3px;
        color: #495057;
        height: auto;
        padding: 0.375rem 0.75rem;
        width: 100%;
    }

    .vue__time-picker >>> input.display-time:focus {
        border: 1px solid #ef635e;
        outline: none;
    }
</style>
